// 📁 src/UserContext.js
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { api } from "./axiosClient";

const UserContext = createContext();

const STORAGE_KEY = "userInfo";

const isValidJwt = (token) =>
  typeof token === "string" && token.split(".").length === 3;

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setAuthHeader = useCallback((token) => {
    if (isValidJwt(token)) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, []);

  const applyUser = useCallback(
    (userData) => {
      const token = userData?.token;

      setAuthHeader(token);

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to save userInfo:", e);
      }

      setUser(userData || null);
      return userData;
    },
    [setAuthHeader]
  );

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);

      if (!saved) {
        setLoading(false);
        return;
      }

      const parsed = JSON.parse(saved);
      const token = parsed?.token;

      if (isValidJwt(token)) {
        setAuthHeader(token);
        setUser(parsed);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to restore user from localStorage:", err);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }, [setAuthHeader]);

  // ✅ email+password login (fallback)
  const loginWithEmail = useCallback(
    async (email, password) => {
      const res = await api.post("/api/users/login", { email, password });
      return applyUser(res.data);
    },
    [applyUser]
  );

  // ✅ register (name, phone, email, password)
  const register = useCallback(
    async (name, phone, email, password) => {
      const res = await api.post("/api/users/register", {
        name,
        phone,
        email,
        password,
      });
      return applyUser(res.data);
    },
    [applyUser]
  );

  // ✅ phone OTP flow
  const sendOtp = useCallback(async (phone, purpose = "login") => {
    const res = await api.post("/api/users/otp/send", { phone, purpose });
    return res.data;
  }, []);

  const verifyOtp = useCallback(
    async (phone, otp) => {
      const res = await api.post("/api/users/otp/verify", {
        phone,
        otp,
        purpose: "login",
      });
      return applyUser(res.data);
    },
    [applyUser]
  );

  const verifyOtpOnly = useCallback(async (phone, otp, purpose = "register") => {
    const res = await api.post("/api/users/otp/verify", { phone, otp, purpose });
    return res.data;
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    setUser(null);
    delete api.defaults.headers.common["Authorization"];
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      register,
      loginWithEmail,
      sendOtp,
      verifyOtp,
      verifyOtpOnly,
      logout,
    }),
    [
      user,
      loading,
      register,
      loginWithEmail,
      sendOtp,
      verifyOtp,
      verifyOtpOnly,
      logout,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
